import React, { useState, useRef, useCallback, forwardRef } from "react";
import { Collapse } from "antd";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getModuleLink } from "@neumetric/blueprint-lib/pages";
import {
  getActiveUsers,
  getDepartments,
  getCurrentUser,
  getPriorities,
} from "@neumetric/blueprint-lib/store";
import { DateTimeUtil, NMCrypto } from "@neumetric/blueprint-lib/utility";
import {
  NMFormPopup,
  Row,
  Col,
  NMForm,
  DataGridView,
  TextButton,
  Button,
} from "@neumetric/blueprint-lib/components";
import {
  getCurrentRisk,
  riskActions,
  isRiskAccepted,
  isRiskArchived,
} from "../../store";

const ViewTasksLink = process.env.REACT_APP_VIEW_TASK_LINK;
const DUE_DATES = {
  emergency: 1,
  urgent: 2,
  critical: 2,
  high: 2,
  medium: 15,
  low: 45,
  minimal: 180,
  least: 360,
};
const ControlFormFields = [
  {
    attribute: "title",
    typeRepresentation: "TEXT",
    classes: { root: "mb-0 w-100 mr-16" },
  },
];

const TaskColumns = [
  {
    dataIndex: "task_id",
    title: "Task ID",
    typeRepresentation: "LINK",
    width: "40%",
  },
  {
    dataIndex: "priority_item",
    typeRepresentation: "DOT",
    width: "130px",
    title: "Priority",
  },
  {
    dataIndex: "responders",
    typeRepresentation: "LIST",
    width: "140px",
    title: "Responders",
  },
  {
    dataIndex: "department",
    typeRepresentation: "TEXT",
    width: "140px",
    title: "Department",
  },
  {
    dataIndex: "status_item",
    typeRepresentation: "DOT",
    width: "130px",
    title: "Status",
  },
];

const TaskFormFields = [
  {
    typeRepresentation: "TEXT",
    label: "Title",
    attribute: "title",
    required: true,
    placeholder: "Title",
    classes: { root: "w-100", label: "w-111-px" },
  },
  {
    label: "Priority",
    typeRepresentation: "DROPDOWN",
    template: "DOT",
    attribute: "priority",
    required: true,
    placeholder: "Priority",
    classes: { root: "w-283-px", label: "w-111-px" },
  },
  {
    typeRepresentation: "DATE",
    label: "Due Date",
    attribute: "due_date",
    required: true,
    placeholder: "Ddd, dd-Mmm-yyyy",
    format: "ddd, DD-MMM-YYYY",
    classes: { root: "w-283-px" },
  },
  {
    typeRepresentation: "DATE",
    label: "Plan",
    attribute: "plan_date",
    format: "ddd, DD-MMM-YYYY",
    readOnly: true,
    classes: { root: "w-200-px", input: "text-placeholder" },
  },
  {
    label: "Description",
    typeRepresentation: "TEXTAREA",
    attribute: "description",
    rows: 3,
    labelAlign: "top",
    required: true,
    placeholder: "Description",
    classes: { root: "w-100", label: "w-111-px" },
  },
  {
    label: "Department",
    typeRepresentation: "DROPDOWN",
    attribute: "dept_name",
    required: true,
    placeholder: "Department",
    classes: { root: "w-283-px", label: "w-111-px" },
  },
  {
    label: "Responder",
    typeRepresentation: "DROPDOWN",
    attribute: "responder_id",
    placeholder: "Responder",
    searchableFlag: true,
    template: "USER",
    labelTemplate: "LIST",
    classes: { root: "w-283-px" },
    required: true,
  },
  {
    typeRepresentation: "CUSTOM",
    component: <div className="w-190-px"></div>,
  },
  {
    label: "Source",
    typeRepresentation: "TEXT",
    attribute: "source",
    readOnly: true,
    classes: { root: "w-100", label: "w-111-px" },
  },
];

const RiskControls = () => {
  const _ref = useRef(null);
  const { riskId } = useParams();
  const dispatch = useDispatch();
  const currentRisk = useSelector(getCurrentRisk);
  const isRiskAccept = useSelector(isRiskAccepted);
  const isRiskArchive = useSelector(isRiskArchived);

  const [state, setState] = useState({
    showAddControl: false,
    controls: [],
  });

  React.useEffect(() => {
    if (riskId && currentRisk && Array.isArray(currentRisk.risk_controls)) {
      setState({ ...state, controls: currentRisk.risk_controls || [] });
    }
  }, [riskId, currentRisk]);

  const handleAddControl = useCallback(() => {
    setState({ ...state, showAddControl: true });
  }, [state]);

  const handleSaveControl = useCallback(() => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const payload = {
      control: {
        title: values.title,
      },
      comment: {
        comment: `Added Control: <span class="fw-600">${values.title}</span>.`,
      },
    };
    const action = riskActions.addRiskControl(payload);
    action.onComplete = handleCancelAddControl;
    dispatch(action);
  }, [state, dispatch]);

  const handleCancelAddControl = useCallback(() => {
    setState({ ...state, showAddControl: false });
  }, [state]);

  const handleDeleteControl = useCallback((control) => {
    const payload = {
      controlId: control.id,
      comment: {
        comment: `Deleted Control: <span class="fw-600">${control.title}</span>.`,
      },
    };
    const action = riskActions.deleteRiskControl(payload);
    action.onComplete = () => {
      handleCancelAddControl();
    };
    dispatch(action);
  }, []);

  return (
    <Col className="w-40">
      <Collapse
        className="w-100"
        defaultActiveKey={["controls"]}
        collapsible="disabled"
      >
        <Collapse.Panel
          key="controls"
          showArrow={false}
          header={
            <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
              Controls
            </span>
          }
        >
          <Col className="pl-20 pr-20">
            {state.controls.length > 0 && (
              <ol className="w-100 pl-16">
                {state.controls.map((control, index) => (
                  <li key={index} className="w-100">
                    <Row justify="between" align="center" className="w-100">
                      <span className="ff-exo2 fs-14 fw-400 line-20 text-filled">
                        {control.title}
                      </span>
                      {Boolean(riskId && currentRisk) &&
                        !isRiskAccept &&
                        !isRiskArchive && (
                          <TextButton
                            icon="DELETE"
                            classes={{ button: "ml-16" }}
                            onClick={() => handleDeleteControl(control)}
                          />
                        )}
                    </Row>
                  </li>
                ))}
              </ol>
            )}
            {state.showAddControl && (
              <Row align="center" className="w-100">
                <NMForm
                  ref={_ref}
                  className="flex-auto"
                  fields={ControlFormFields}
                  formData={{}}
                  direction="horizontal"
                />
                <TextButton
                  icon="CHECK_CONFIRM"
                  classes={{ button: "ml-16" }}
                  onClick={handleSaveControl}
                />
                <TextButton
                  icon="CANCEL"
                  variant="CAUTION"
                  classes={{ button: "ml-8" }}
                  onClick={handleCancelAddControl}
                />
              </Row>
            )}

            {!state.showAddControl && !isRiskAccept && !isRiskArchive && (
              <TextButton
                label="Add Control"
                classes={{ label: "fw-400", icon: "fw-400" }}
                icon="ADD"
                onClick={handleAddControl}
              />
            )}
          </Col>
        </Collapse.Panel>
      </Collapse>
    </Col>
  );
};

const CreateRiskTask = (props) => {
  const dispatch = useDispatch();
  const currentRisk = useSelector(getCurrentRisk);
  const department = useSelector(getDepartments);
  const priorities = useSelector(getPriorities);
  const users = useSelector(getActiveUsers);
  const arcDeptResponders = [];
  const user = useSelector(getCurrentUser);
  const ref = React.useRef(null);
  const [isSubmitting, setIsSubmitting] = React.useState(true);

  React.useEffect(() => {
    if (currentRisk) {
      ref.current.updateFormData(getInitialValues());
      setIsSubmitting(false);
    }
  }, [currentRisk]);

  const getFields = () => {
    return TaskFormFields.map((_) => {
      return { ..._, labelAlign: _.labelAlign || "middle" };
    });
  };

  const getInitialValues = React.useCallback(() => {
    return {
      plan_date: new Date(),
      title: currentRisk.title,
      priority: currentRisk.severity,
      dept_name: currentRisk.department,
      source: `Risk/${currentRisk.module_risk_id}: ${currentRisk.title}`,
      description: "No Description Provided",
      ...getDependentValues("priority", currentRisk.severity),
      ...getDependentValues("dept_name", currentRisk.department),
    };
  }, [currentRisk]);

  const getOptions = (attribute) => {
    switch (attribute) {
      case "responder_id":
        return users; // add current logged in user also as it is the default responder when no task responder is defined for the particular department
      case "dept_name":
        return department;
      case "priority":
        return priorities || [];
      default:
        return [];
    }
  };

  const handleCreate = (task) => {
    delete task.source;
    task.plan_date = new Date(task.plan_date).toISOString();
    task.due_date = new Date(task.due_date).toISOString();
    setIsSubmitting(true);
    const action = riskActions.createRiskTask({ task: task });
    action.onComplete = () => {
      setIsSubmitting(false);
      props.onCancel();
    };
    action.onFailure = () => {
      setIsSubmitting(false);
    };
    dispatch(action);
  };

  const getDependentValues = (attribute, value) => {
    const newValues = {};
    if (attribute === "priority") {
      const duedate = DUE_DATES[String(value).toLowerCase()];
      if (duedate) {
        const dayjs = new DateTimeUtil().getInstance();
        const date = dayjs.add(duedate, "day");
        newValues.due_date = date.toDate();
        newValues.plan_date = date.toDate();
      }
    } else if (attribute === "dept_name") {
      const matchingResponder = arcDeptResponders.find(
        (resp) => resp.department_name === value
      );
      const responderId = matchingResponder?.ent_usr_id || user?.id;
      if (responderId) {
        newValues.responder_id = responderId;
      }
    }
    return newValues;
  };

  const handleOnValueChange = (attribute, value) => {
    const newValues = getDependentValues(attribute, value);
    if (Object.keys(newValues).length > 0) {
      ref.current.updateFormData(newValues);
    }
  };
  return (
    <NMFormPopup
      ref={ref}
      visible={true}
      isSubmitting={isSubmitting}
      getOptions={getOptions}
      classes={{ root: "create-task-popup" }}
      title="New Task"
      closeOnEscape={true}
      variant="INFO"
      width="1000px"
      fields={getFields()}
      formData={{}}
      onSubmit={handleCreate}
      onChange={handleOnValueChange}
      onModalClose={props.onCancel}
      actions={[
        {
          label: "Cancel",
          btnType: "OUTLINED",
          onClick: props.onCancel,
          icon: "CANCEL",
        },
        {
          label: "Create New Task",
          btnType: "CONTAINED",
          type: "submit",
          icon: "ADD",
        },
      ]}
    />
  );
};

const RiskTasks = () => {
  const currentRisk = useSelector(getCurrentRisk);
  const currentUser = useSelector(getCurrentUser);

  const getTaskLink = (taskGUID) => {
    let arclink = getModuleLink(ViewTasksLink, currentUser);
    let redirect = { path: `/T/${taskGUID}` };
    redirect = NMCrypto.encrypt(redirect);
    return `${arclink}&redirect=${redirect}`;
  };

  const getCellSemantics = (column, record) => {
    const sematics = {};
    if (column.dataIndex === "task_id") {
      sematics.link = getTaskLink(record.guid);
    }
    return sematics;
  };

  return (
    <Col className="w-58 mr-16 ">
      <DataGridView
        classes={{ root: "h-300-px task-grid-view" }}
        columns={TaskColumns}
        dataSource={currentRisk.tasks}
        EmptyComponent={
          <Col className="w-100 h-200-px" align="center" justify="center">
            There are no tasks right now!
          </Col>
        }
        getCellSemantics={getCellSemantics}
      />
    </Col>
  );
};

export const MitigationSection = forwardRef((props, ref) => {
  const isRiskAccept = useSelector(isRiskAccepted);
  const isRiskArchive = useSelector(isRiskArchived);
  const currentRisk = useSelector(getCurrentRisk);
  const currentUser = useSelector(getCurrentUser);
  const [state, setState] = useState({
    showCreateTaskPopup: false,
  });

  const handleAddTask = () => {
    setState({ ...state, showCreateTaskPopup: true });
  };

  const handleCloseCreateTaskPopup = () => {
    setState({ ...state, showCreateTaskPopup: false });
  };

  const handleViewTasks = (e) => {
    e.stopPropagation();

    let modLink = getModuleLink(ViewTasksLink, currentUser);
    const redirectData = {
      path: "/T",
    };
    const filter = {
      type: {
        label: "Risk",
        id: "risk",
        fetchKey: "risk_id",
        queryKey: "risk",
        key: "risk",
        value: "Risk",
      },
      search: {
        label: currentRisk.title,
        id: currentRisk.id,
        guid: currentRisk.guid,
      },
    };
    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    searchParams.set("filter", encodeURIComponent(JSON.stringify(filter)));
    redirectData.search = `?${searchParams.toString()}`;
    let redirect = NMCrypto.encrypt(redirectData);
    modLink += `&redirect=${redirect}`;
    window.open(modLink, "_blank", "noopener,noreferrer");
  };

  return (
    <Collapse
      className="w-100 mt-16 mitigation-section"
      defaultActiveKey={["mitigation"]}
      collapsible="disabled"
    >
      <Collapse.Panel
        key="mitigation"
        showArrow={false}
        header={
          <Row className="w-100" justify="between">
            <span className="ff-exo2 fs-18 fw-600 line-28 text-filled">
              Mitigation
            </span>
            <Row>
              <Button
                label="View Tasks"
                disabled={currentRisk.tasks.length === 0}
                classes={{ button: "mr-16" }}
                onClick={handleViewTasks}
              />
              {!isRiskAccept && !isRiskArchive && (
                <Button label="Add Task" icon="ADD" onClick={handleAddTask} />
              )}
            </Row>
          </Row>
        }
      >
        <Row className="w-100" justify="between">
          <RiskControls />
          <RiskTasks />
        </Row>
      </Collapse.Panel>
      {state.showCreateTaskPopup && (
        <CreateRiskTask onCancel={handleCloseCreateTaskPopup} />
      )}
    </Collapse>
  );
});
